/**
 * The game's colors, and the icon shape that goes with each one.
 *
 * Accessibility matters here. Some players can't tell certain colors apart
 * (color blindness affects about 1 in 12 men), so color alone must never be
 * the only way to match things. That's a published accessibility rule
 * (WCAG 1.4.1, "Use of Color"). Every color is therefore paired with its own
 * icon shape, drawn on top.
 *
 * The other rule is contrast: icons and text must stand out clearly from
 * what's behind them. The `luminance` and `contrast` functions at the bottom
 * implement the official WCAG formulas, and the tests use them to check
 * every color.
 */

export type Shape =
	| "circle"
	| "square"
	| "triangle"
	| "star"
	| "diamond"
	| "plus"
	| "ring"
	| "heart"
	| "hexagon"
	| "drop"
	| "moon"
	| "hourglass"
	| "cross"
	| "half"
	| "bolt"
	| "chevron"
	| "pentagon"
	| "frame"
	| "outlineTriangle"
	| "outlineDiamond"
	| "arrow"
	| "bar"
	| "clover"
	| "spade"
	| "flag"
	| "leaf"
	| "tee"
	| "trapezoid"
	| "parallelogram"
	| "cloud"
	| "note"
	| "trio";

/**
 * One game color: a name (for people reading the code), the fill color, and
 * the icon drawn on it.
 */
export interface Swatch {
	name: string;
	fill: string;
	shape: Shape;
}

/**
 * Every color the game can use, in order. A color's number in the game is its
 * position in this list. A level with N colors uses the first N, so the most
 * distinct colors come first.
 */
export const PALETTE: Swatch[] = [
	{ name: "red", fill: "#e5484d", shape: "circle" },
	{ name: "blue", fill: "#3e9cf0", shape: "square" },
	{ name: "green", fill: "#46a758", shape: "triangle" },
	{ name: "yellow", fill: "#f5c518", shape: "star" },
	{ name: "purple", fill: "#a374dc", shape: "diamond" },
	{ name: "orange", fill: "#f76b15", shape: "plus" },
	{ name: "teal", fill: "#12a594", shape: "ring" },
	{ name: "pink", fill: "#d6409f", shape: "heart" },
	{ name: "lime", fill: "#8fd14f", shape: "hexagon" },
	{ name: "cyan", fill: "#2ec4e6", shape: "drop" },
	{ name: "indigo", fill: "#8b93ff", shape: "moon" },
	{ name: "magenta", fill: "#e264e8", shape: "hourglass" },
	{ name: "coral", fill: "#ff8f7a", shape: "cross" },
	{ name: "tan", fill: "#cfa57c", shape: "half" },
	{ name: "silver", fill: "#b7bfcc", shape: "bolt" },
	{ name: "mint", fill: "#7fe0bf", shape: "chevron" },
	{ name: "mauve", fill: "#c890b8", shape: "pentagon" },
	{ name: "apricot", fill: "#ffa64d", shape: "frame" },
	{ name: "lemon", fill: "#fff27a", shape: "outlineTriangle" },
	{ name: "periwinkle", fill: "#b8c0ff", shape: "outlineDiamond" },
	{ name: "rose", fill: "#ff6f91", shape: "arrow" },
	{ name: "cream", fill: "#f2e6b8", shape: "bar" },
	{ name: "olive", fill: "#b4bd4a", shape: "clover" },
	{ name: "aqua", fill: "#8ff0f5", shape: "spade" },
	{ name: "orchid", fill: "#f2a8f2", shape: "flag" },
	{ name: "white", fill: "#f4f6fa", shape: "leaf" },
	{ name: "khaki", fill: "#d4c87a", shape: "tee" },
	{ name: "sky", fill: "#8fd0ff", shape: "trapezoid" },
	{ name: "blush", fill: "#ffb8cf", shape: "parallelogram" },
	{ name: "jade", fill: "#3fcf8e", shape: "cloud" },
	{ name: "chartreuse", fill: "#c4ec5a", shape: "note" },
	{ name: "gold", fill: "#e8b53a", shape: "trio" },
];

/** The two color themes. */
export type ThemeName = "dark" | "light";

/**
 * The colors the game draws with, apart from the game colors themselves:
 * the track, grid background, text, and so on. There's one set per theme.
 *
 * The page's own background isn't here: the canvas is transparent where
 * nothing is drawn, so the page's `--bg` (in src/theme.css) shows through.
 * That keeps the background color in exactly one place.
 *
 * The 32 game colors stay the same in both themes, so a color means the same
 * thing (and a picture looks the same) whichever theme is in use. They're all
 * fairly light, which works on the dark theme's backgrounds; on the light
 * theme's light backgrounds, cells and blocks get a dark `outline` instead, so
 * their edges still stand out clearly. The tests check every one of these
 * pairings against the accessibility contrast rules.
 */
export interface Theme {
	/** The track's band. */
	track: string;
	/** Outlines of empty tray slots. */
	trackEdge: string;
	/** Behind the grid's cells. */
	gridBackground: string;
	/** Titles, counters, outlines of blocks on the track, link bars, shots. */
	text: string;
	/** Icons and numbers drawn on top of cells and blocks. */
	ink: string;
	/** The track counter when the track is full. */
	alert: string;
	/** Edge drawn around cells and blocks, or `null` for none. */
	outline: string | null;
	/**
	 * How opaque (0 to 1) the layer behind the win/loss message is. The layer
	 * is the page's background color (`--bg` in src/theme.css).
	 */
	overlayOpacity: number;
}

/**
 * The color values for each theme. `Record<ThemeName, Theme>` means "an object
 * with one `Theme` for every theme name", so TypeScript reports an error if a
 * theme is added to `ThemeName` but not defined here.
 */
export const THEMES: Record<ThemeName, Theme> = {
	dark: {
		track: "#1b1f2a",
		trackEdge: "#626d84",
		gridBackground: "#141821",
		text: "#e6e8ee",
		ink: "#0b0d12",
		alert: "#e5484d",
		// The game colors already stand out against the dark backgrounds.
		outline: null,
		overlayOpacity: 0.65,
	},
	light: {
		track: "#d9dee7",
		trackEdge: "#737d90",
		gridBackground: "#ffffff",
		text: "#161a24",
		ink: "#0b0d12",
		alert: "#c0262c",
		outline: "#363d4d",
		overlayOpacity: 0.82,
	},
};

/**
 * The swatch for a color number. The `%` (remainder) wraps out-of-range
 * numbers back into the list, so it never fails.
 */
export function swatchFor(index: number): Swatch {
	return PALETTE[index % PALETTE.length] as Swatch;
}

/**
 * How bright a color looks to the human eye, from 0 (black) to 1 (white),
 * using the WCAG "relative luminance" formula. Green counts for much more
 * than blue, because our eyes are most sensitive to green.
 */
export function luminance(hex: string): number {
	const channel = (offset: number) => {
		const c = Number.parseInt(hex.slice(offset, offset + 2), 16) / 255;
		return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
	};
	return 0.2126 * channel(1) + 0.7152 * channel(3) + 0.0722 * channel(5);
}

/**
 * How strongly two colors stand out from each other, from 1 (identical) to
 * 21 (black on white), using the WCAG formula. Accessibility guidelines ask
 * for at least 4.5 for small text and 3 for shapes.
 */
export function contrast(a: string, b: string): number {
	const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x) as [
		number,
		number,
	];
	return (hi + 0.05) / (lo + 0.05);
}
